import Axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { countryListAlpha2 } from '../countries';
import BadenBowerLogo from '../images/BB-logo-3.png';
import AirWallexLogo from '../images/airwallex-logo.jpg';
// STEP #1: At the start of your file, import airwallex-payment-elements package
import {
  EventDetail,
  confirmPaymentIntent,
  createElement,
  createPaymentConsent,
  getElement,
  loadAirwallex,
} from 'airwallex-payment-elements';
import { useParams } from 'react-router-dom';
import {
  IPaymentIntent,
  IPaymentResponse,
} from '../interfaces/paymentData.interfaces';

const Payment: React.FC = () => {
  let { id } = useParams();
  const today = new Date();
  const countries = countryListAlpha2();

  // Example: element ready states, controls the display for when elements are successfully mounted
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [npaymentIntent, setPaymentIntent] = useState<IPaymentIntent>();
  const [paymentConsent, setPaymentConsent] = useState();

  const [paymentConsentId, setPaymentConsentId] = useState<string>();
  const [cardNumberReady, setCardNumberReady] = useState<boolean>(false);
  const [cvcReady, setCvcReady] = useState<boolean>(false);
  const [expiryReady, setExpiryReady] = useState<boolean>(false);

  const [paymentStatusOk, setPaymentStatusOk] = useState<boolean>();

  // Example: element validation state, checks if each field is completed by the shopper
  const [cardNumberComplete, setCardNumberComplete] = useState<
    undefined | boolean
  >(false);
  const [cvcComplete, setCvcComplete] = useState<undefined | boolean>(false);
  const [expiryComplete, setExpiryComplete] = useState<undefined | boolean>(
    false
  );

  const [hasFormErrors, setHasFormErrors] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    cardName: '',
    country: '',
    state: '',
    postalCode: '',
    phone: '',
    email: '',
  });

  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [city, setCity] = useState<string>();
  const [country, setCountry] = useState<string>();
  const [street, setStreet] = useState<string>();
  const [postalCode, setPostalCode] = useState<string>();
  const [cardName, setCardName] = useState<string>();
  const [state, setState] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [email, setEmail] = useState<string>();

  const [isSubmitting, setIsSubmitting] = useState(false); // Example: show submission processing state
  const [errorMessage, setErrorMessage] = useState(''); // Example: set error state
  const [paymentResponse, setPaymentResponse] = useState<any>();
  const [paymentResult, setPaymentResult] = useState<any>();
  // const [paymentStatus, setPaymentStatus] = useState("ok") // Example: set payment status state
  const [paymentDetails, setPaymentDetails] = useState<IPaymentResponse>(); // Example: set payment data st
  const [apiStatus, setApiStatus] = useState();
  const [inputErrorMessage, setInputErrorMessage] = useState({
    cardNumber: '',
    expiry: '',
    cvc: '',
    firstName: '',
  }); // Example: set api status state

  useEffect(() => {
    // STEP #2: Initialize Airwallex on mount with the appropriate production environment and other configurations
    loadAirwallex({
      env: 'prod', // Can choose other production environments, 'staging | 'demo' | 'prod'
      origin: window.location.origin, // Setup your event target to receive the browser events message
      fonts: [
        // Customizes the font for the payment elements
        {
          src: 'https://checkout.airwallex.com/fonts/CircularXXWeb/CircularXXWeb-Regular.woff2',
          family: 'AxLLCircular',
          weight: 400,
        },
      ],
      // For more detailed documentation at https://github.com/airwallex/airwallex-payment-demo/tree/master/docs#loadAirwallex
    }).then(() => {
      // STEP #4, 5: Create and mount the individual card elements
      createElement('cardNumber')?.mount('cardNumber'); // This 'cardNumber' id MUST MATCH the id on your cardNumber empty container created in Step 3
      createElement('cvc')?.mount('cvc'); // Same as above
      createElement('expiry')?.mount('expiry'); // Same as above
    });

    const fetchPaymentDetails = async (
      paymentId: any
    ): Promise<IPaymentResponse> => {
      const response = Axios.get<IPaymentResponse>(
        `${process.env.REACT_APP_API_URL}/api/payments/${paymentId}`
      )
        .then((response: AxiosResponse<IPaymentResponse, any>) => {
          if (response.status >= 200 && response.status < 300) {
            return response.data as IPaymentResponse;
          }
          throw response;
        })
        .catch((error: any) => {
          setApiStatus(error.response?.status);
          if (error.response?.data?.error) {
            throw Error(error.response.data.error);
          }
          throw error;
        });

      return response;
    };

    fetchPaymentDetails(id)
      .then(function (data) {
        setPaymentDetails(data);
        setPaymentStatusOk(true);
        // setElementShow(true)
      })
      .catch((err) => {
        setPaymentStatusOk(false);
        setErrorMessage(err.message);
        // setPaymentStatus("paymentNotFound")
      });

    // STEP #7: Add an event handler to ensure the element is mounted
    const onReady = (event: CustomEvent): void => {
      const { type } = event.detail as EventDetail;
      if (type === 'cardNumber') {
        setCardNumberReady(true);
      }
      if (type === 'cvc') {
        setCvcReady(true);
      }
      if (type === 'expiry') {
        setExpiryReady(true);
      }
    };

    // STEP #8: Add an event listener to listen to the changes in each of the input fields
    const onChange = (event: CustomEvent) => {
      const { type, complete } = event.detail as EventDetail;
      if (type === 'cardNumber') {
        setCardNumberComplete(complete);
      }
      if (type === 'cvc') {
        setCvcComplete(complete);
      }
      if (type === 'expiry') {
        setExpiryComplete(complete);
      }
    };
    // STEP #9: Add an event listener to get input focus status
    const onFocus = (event: CustomEvent) => {
      const { type } = event.detail;
      setInputErrorMessage({
        ...inputErrorMessage,
        [type]: '', // Example: clear input error message
      });
      // Customize your input focus style by listen onFocus event
    };
    // STEP #10: Add an event listener to show input error message when finish typing
    const onBlur = (event: CustomEvent) => {
      const { type, error } = event.detail;
      setInputErrorMessage({
        ...inputErrorMessage,
        [type]: error?.message ?? JSON.stringify(error),
      });
    };
    window.addEventListener('onReady', onReady as EventListener);
    window.addEventListener('onChange', onChange as EventListener); // Can also using onBlur
    window.addEventListener('onBlur', onBlur as EventListener);
    window.addEventListener('onFocus', onFocus as EventListener);
    return () => {
      window.removeEventListener('onReady', onReady as EventListener);
      window.removeEventListener('onChange', onChange as EventListener);
      window.removeEventListener('onBlur', onBlur as EventListener);
      window.removeEventListener('onFocus', onFocus as EventListener);
    };
  }, []);

  // Example: combine all element ready states
  const allElementsReady = cardNumberReady && cvcReady && expiryReady;
  // Example: combine all element complete states
  const allElementsComplete =
    cardNumberComplete && cvcComplete && expiryComplete;

  // Get the customer Id
  const getCustomerId = async () => {
    if (!customerId) {
      const result = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/payments/${id}/customer`,
        {
          data: {
            firstName,
            lastName,
            city,
            street,
            country,
            postalCode,
            state,
            phone,
            email,
          },
        }
      );
      return result.data;
    }
  };

  // Generate the payment consent
  const generatePaymentConsent = async (
    paymentIntent: any,
    customerId: string,
    element: any
  ) => {
    return await createPaymentConsent({
      // element
      element: element,

      // payment intent id
      intent_id: paymentIntent.intentId,

      // client secret
      client_secret: paymentIntent.clientSecret,

      // customer id
      customer_id: customerId,

      // currency
      currency: paymentIntent.currency,

      // next triggered by
      next_triggered_by: 'merchant',

      // merchant_trigger_reason
      merchant_trigger_reason: 'unscheduled',

      // billing details - required for risk
      billing: {
        // address - required
        address: {
          // city - required
          city: city,

          // postcode - required
          postcode: postalCode,

          // state - required
          state: state,

          // street - required
          street: street,

          // country code - required
          country_code: country,
        },

        // first name - required
        first_name: firstName,

        // last name - required
        last_name: lastName,

        // phone number - required
        phone_number: phone,

        // email - required
        email: email,
      },
    })
      .then((value: any) => {
        /**
         * ...Handle confirm response
         */
        setIsSubmitting(false); // Example: sets loading state
        setPaymentResult('Payment was processed successfully'); // Example: set payment response
      })
      .catch((error) => {
        /**
         * ... Handle error response
         */
        setIsSubmitting(false); // sets loading state
        setErrorMessage(error.message ?? JSON.stringify(error)); // set error message
        setPaymentStatusOk(false);
      });
  };

  const validateForm = () => {
    let hasErrors = false;
    let err = {
      firstName: '',
      lastName: '',
      city: '',
      street: '',
      country: '',
      cardName: '',
      state: '',
      postalCode: '',
      phone: '',
      email: '',
    };

    if (firstName === null || (firstName ?? '').length <= 1) {
      err = { ...err, ...{ firstName: 'First name is required' } };
      hasErrors = true;
    }

    if (lastName === null || (lastName ?? '').length <= 1) {
      err = { ...err, ...{ lastName: 'Last name is required' } };
      hasErrors = true;
    }

    if (city === null || (city ?? '').length <= 1) {
      err = { ...err, ...{ city: 'City is required' } };
      hasErrors = true;
    }

    if (street === null || (street ?? '').length <= 1) {
      err = { ...err, ...{ street: 'Street is required' } };
      hasErrors = true;
    }

    if (cardName === null || (cardName ?? '').length <= 1) {
      err = { ...err, ...{ cardName: 'Card Name is required' } };
      hasErrors = true;
    }

    if (country === null || (country ?? '').length <= 1) {
      err = { ...err, ...{ country: 'Country is required' } };
      hasErrors = true;
    }

    // if (state === null || (state ?? '').length <= 1) {
    //   err = { ...err, ...{ state: 'State is required' } };
    //   hasErrors = true;
    // }

    // if (postalCode === null || (postalCode ?? '').length <= 1) {
    //   err = { ...err, ...{ postalCode: 'Postal/Zip Code is required' } };
    //   hasErrors = true;
    // }

    // if (phone === null || (phone ?? '').length <= 1) {
    //   err = { ...err, ...{ phone: 'Phone is required' } };
    //   hasErrors = true;
    // }

    if (email === null || (email ?? '').length <= 1) {
      err = { ...err, ...{ email: 'Email is required' } };
      hasErrors = true;
    }

    // console.log(formErrors)
    if (!hasErrors) {
      setHasFormErrors(false);
      setFormErrors({
        ...err,
      });
    } else {
      setFormErrors({
        ...err,
      });

      setHasFormErrors(true);
    }
  };

  // STEP ##6a: Add a button handler to trigger the payment request
  const triggerConfirm = async (): Promise<void> => {
    setIsSubmitting(true); // Example: set loading state
    setErrorMessage(''); // Example: reset error message

    const card = getElement('cardNumber');
    if (card) {
      // STEP #6b: Get the customer id
      const paymentRequestMeta = await getCustomerId();

      if (paymentDetails?.paymentInfo.recurring === 'MONTHLY') {
        // get the payment consent for recurring payment
        await generatePaymentConsent(
          paymentRequestMeta.paymentIntent,
          paymentRequestMeta.customerId,
          card
        );
      }

      if (paymentDetails?.paymentInfo.recurring !== 'MONTHLY') {
        confirmPaymentIntent(
          Object.assign({
            element: card,

            // payment intent id
            id: paymentRequestMeta?.paymentIntent?.intentId,

            // client secret
            client_secret: paymentRequestMeta?.paymentIntent?.clientSecret,

            // customer id
            customer_id: paymentRequestMeta?.customerId?.toString(),

            // Add other payment confirmation details, see docs here: https://github.com/airwallex/airwallex-payment-demo/tree/master/docs
            payment_method_options: {
              card: {
                auto_capture: true,

                // billing details - required for risk
                billing: {
                  // address - required
                  address: {
                    // city - required
                    city: city,

                    // country - required
                    postalCode: postalCode,

                    // state - required
                    state: state,

                    // street - required
                    street: street,

                    // country code - required
                    country_code: country,
                  },

                  // first name - required
                  first_name: firstName,

                  // last name - required
                  last_name: lastName,

                  // phone number - required
                  phone_number: phone,

                  // email - required
                  email: email,
                },
              },
            },
          })
        )
          // STEP ##6b: Listen to the request success response
          .then((response) => {
            /**
             * ...Handle confirm response
             */
            setIsSubmitting(false); // Example: sets loading state
            setPaymentResult(response); // Example: set payment response
          })
          // STEP ##6c: Listen to the request failure response
          .catch((error) => {
            /**
             * ... Handle error response
             */
            setIsSubmitting(false); // Example: sets loading state
            setErrorMessage(error.message ?? JSON.stringify(error)); // Example: set error message
            setPaymentStatusOk(false);
          });
        // })
      }
    }
  };

  const handleCountryChange = (e: any) => {
    // console.log("country set to ",e.target.value)
    setCountry(e.target.value);
    validateForm();
  };

  const paymentForm = () => {
    return (
      <div className="flex">
        <div className="flex flex-col px-0 py-0  md:shadow-lg rounded-lg border w-full md:max-w-2xl">
          <div className="flex flex-col bg-gray-100 px-8 py-8 text-left border-b">
            <div className="font-bold pb-3">Payment Details</div>
            <div className="pt-1 pb-1">
              <span className="font-bold">
                ${((paymentDetails?.paymentInfo.amount || 0) / 100).toFixed(2)}
                &nbsp;{paymentDetails?.paymentInfo?.currency} is what you will
                be charged today.
              </span>
            </div>
            {paymentDetails?.memo && (
              <div className="pt-1 pb-1">
                {paymentDetails?.memo.split('\n').map(function (item, idx) {
                  return (
                    <span key={idx}>
                      {item}
                      <br />
                    </span>
                  );
                })}
              </div>
            )}
            {paymentDetails?.paymentInfo.recurring === 'MONTHLY' && (
              <div className="pt-1 pb-1">
                You will be charged $
                {((paymentDetails?.paymentInfo.amount || 0) / 100).toFixed(2)}
                &nbsp;{paymentDetails?.paymentInfo?.currency} each month, and
                you can cancel anytime by contacting us.
              </div>
            )}
          </div>
          <div className="flex flex-col px-8 py-8 w-auto">
            <div className="flex flex-row text-left pb-2">
              {/* customer details */}
              <div className="flex flex-col md:flex-row flex-auto">
                <div className="flex flex-col grow md:pr-1 pb-2 md:pb-0">
                  <div className="flex flex-rows pb-1">
                    <div className="font-bold mr-2">First Name</div>
                    <div className="text-xs pt-1.5">(required)</div>
                  </div>
                  <input
                    type="text"
                    className="border border-gray-300 p-1 rounded bg-gray-50"
                    id="firstName"
                    placeholder="First Name"
                    onChange={(event) => setFirstName(event.target.value)}
                    onBlur={validateForm}
                    onFocus={validateForm}
                  />
                  <p style={{ color: 'red' }}>{formErrors.firstName}</p>
                </div>
                <div className="flex flex-col grow md:pl-1">
                  <div className="flex flex-rows pb-1">
                    <div className="font-bold mr-2">Last Name</div>
                    <div className="text-xs pt-1.5">(required)</div>
                  </div>
                  <input
                    type="text"
                    className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                    id="lastName"
                    placeholder="Last Name"
                    onChange={(event) => setLastName(event.target.value)}
                    onBlur={validateForm}
                    onFocus={validateForm}
                  />
                  <p style={{ color: 'red' }}>{formErrors.lastName}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">Street</div>
                <div className="text-xs pt-1.5">(required)</div>
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="street"
                placeholder="Street"
                onChange={(event) => setStreet(event.target.value)}
                onBlur={validateForm}
                onFocus={validateForm}
              />
              <p style={{ color: 'red' }}>{formErrors.street}</p>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">City</div>
                <div className="text-xs pt-1.5">(required)</div>
              </div>{' '}
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="city"
                placeholder="City"
                onChange={(event) => setCity(event.target.value)}
                onBlur={validateForm}
                onFocus={validateForm}
              />
              <p style={{ color: 'red' }}>{formErrors.city}</p>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">State</div>
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="state"
                placeholder="State"
                onChange={(event) => setState(event.target.value)}
                onBlur={validateForm}
                onFocus={validateForm}
              />
              <p style={{ color: 'red' }}>{formErrors.state}</p>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">Postal/Zip code</div>
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="city"
                placeholder="Postal/Zip code"
                onChange={(event) => setPostalCode(event.target.value)}
                onBlur={validateForm}
                onFocus={validateForm}
              />
              <p style={{ color: 'red' }}>{formErrors.postalCode}</p>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">Country</div>
                <div className="text-xs pt-1.5">(required)</div>
              </div>
              <select
                className="bw-full border border-gray-300 p-1 rounded bg-gray-50"
                onChange={handleCountryChange}
                onBlur={validateForm}
                onFocus={validateForm}
              >
                <option>Select Country</option>
                {Object.entries(countries).map(([key, value]) => (
                  <option value={key}>{value}</option>
                ))}
              </select>
              <p style={{ color: 'red' }}>{formErrors.country}</p>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">Email</div>
                <div className="text-xs pt-1.5">(required)</div>
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="email"
                placeholder="Email"
                onChange={(event) => setEmail(event.target.value)}
                onBlur={validateForm}
                onFocus={validateForm}
              />
              <p style={{ color: 'red' }}>{formErrors.email}</p>
            </div>
            <div className="flex flex-col pb-3">
              <div className="flex flex-rows pb-1">
                <div className="font-bold mr-2">Phone</div>
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="phone"
                placeholder="Phone"
                onChange={(event) => setPhone(event.target.value)}
                onBlur={validateForm}
                onFocus={validateForm}
              />
              <p style={{ color: 'red' }}>{formErrors.phone}</p>
            </div>
            <hr className="mt-5 mb-5"></hr>
            {/* name on card */}
            <div className="flex flex-col text-left pb-3">
              <div className="pb-1 font-bold">Name on card</div>
              <div className="text-xs pb-2">
                Please enter the name which appears on the credit card
                (required)
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="cardName"
                placeholder="Name on card"
                onBlur={validateForm}
                onFocus={validateForm}
                onChange={(event) => setCardName(event.target.value)}
              />
              <p style={{ color: 'red' }}>{formErrors.cardName}</p>
            </div>

            <div className="flex flex-col text-left">
              <div className="pb-1 font-bold">Card Number</div>
              <div className="text-xs pb-2">
                Please enter a valid credit card number (required)
              </div>
              <div
                className="w-full border border-gray-300 p-1 rounded bg-gray-50"
                id="cardNumber"
              />
              <p style={{ color: 'red' }}>{inputErrorMessage.cardNumber}</p>
            </div>

            <div className="flex flex-col md:flex-row flex-auto">
              <div className="flex flex-col">
                <div className="pb-1 pt-4 font-bold">Expiry Date</div>
                <div className="text-xs pb-2">(required)</div>
                <div
                  id="expiry"
                  className="w-32 border border-gray-300 p-1 rounded bg-gray-50"
                />
                <p style={{ color: 'red' }}>{inputErrorMessage.expiry}</p>
              </div>
              <div className="flex flex-col md:ml-5">
                <div className="pb-1 pt-4 font-bold">CVV</div>
                <div className="text-xs pb-2">(required)</div>
                <div
                  id="cvc"
                  className="w-10 border border-gray-300 right-0 p-1 rounded bg-gray-50"
                />
                <p style={{ color: 'red' }}>{inputErrorMessage.cvc}</p>
              </div>
            </div>
            {/* <p style={{ color: "red" }}>{inputErrorMessage}</p> */}
            <button
              className="px-8 py-2 bg-blue-700 mt-4 rounded-lg text-white lowercase"
              onClick={triggerConfirm}
              disabled={!allElementsComplete || isSubmitting || hasFormErrors} // Example: disables button when submitting to prevent resubmission
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  };

  const paymentResultDisplay = () => {
    if (!paymentResult) return;

    return (
      <div>
        <div className="flex flex-col px-0 py-0 shadow-lg rounded-lg border">
          <div className="flex flex-col bg-white px-8 py-8 text-left border-b justify-center items-center rounded">
            <div className="font-bold">Transaction Response</div>
            <div className="mt-5">
              Your transaction was
              <div className="pill success font-bold items-center text-center rounded mt-5">
                successful
              </div>
            </div>
            <div className="mt-5">
              <span className="font-bold">Transaction ref:</span>
              <span className="ml-3">{id}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const paymentResultErrorDisplay = () => {
    if (!errorMessage) return;

    return (
      <div>
        <div className="flex flex-col px-0 py-0 md:shadow-lg rounded-lg border mb-10">
          <div className="flex flex-col bg-white px-8 py-8 text-left border-b justify-center items-center rounded">
            {errorMessage && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <div className="font-bold mb-2">
                  We are unable to process your payment
                </div>
                {errorMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-cols items-center justify-center md:max-w-lg m-auto">
        <div>
          <div className="flex justify-center items-center flex-col">
            <img src={BadenBowerLogo} alt="badenbower-logo" className="mb-5" />
          </div>

          {!allElementsReady && (
            <p className="flex justify-center">Loading...</p>
          )}
          {/* Example below: display response message block */}
          {errorMessage && paymentResultErrorDisplay()}
          {paymentResult && paymentResultDisplay()}
          <div className={`w-auto ${paymentResponse ? 'visible' : 'hidden'}`}>
            {paymentResult && paymentResultDisplay()}
          </div>
          <div
            className={`grow ${
              allElementsReady && paymentStatusOk ? 'visible' : 'hidden'
            }`}
          >
            {!paymentResult && paymentForm()}
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center flex-col">
        <img src={AirWallexLogo} alt="airwallex-logo" className="mt-10" />
        <div className="font-bold text-sm m-1">
          Payment processed securely by&nbsp;
          <a
            href="https://www.airwallex.com/"
            target="_blank"
            rel="noreferrer"
            className="text-blue-700"
          >
            Airwallex
          </a>
        </div>
      </div>
    </div>
  );
};

export default Payment;
